import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuthStore } from '@/store/auth-store'
import { authService } from '@/services/auth-service'
import { GradientDots } from '@/components/ui/gradient-dots'

export function RegisterPage() {
  const [searchParams] = useSearchParams()
  // ?c= is the new exchange-code link (audit #6 hotfix).
  // ?token= is the legacy JWT-in-URL link — kept alive for the 7-day soak
  // window so already-mailed invitations don't break.
  const code = searchParams.get('c') || ''
  const legacyToken = searchParams.get('token') || ''
  const credential = code || legacyToken

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const { login } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (!credential) {
      setError('Invalid or missing invitation link. Please check your email and try again.')
    }
  }, [credential])

  // Strip the credential from the URL the moment the page mounts so it
  // doesn't end up in browser history, Sentry replay, or Referer.
  useEffect(() => {
    if (!credential) return
    try {
      window.history.replaceState(null, '', '/register')
    } catch {
      // Best-effort scrub; not fatal if it fails.
    }
    // Only run once on mount — searchParams stays available via the
    // captured `credential` closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)

    try {
      // Backend `/auth/register-invite` accepts both legacy JWTs and new
      // opaque exchange codes — we pass whichever the URL carried.
      const result = await authService.registerWithInvite(credential, password)
      login(result.user, result.token)
      navigate('/dashboard', { replace: true })
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data
      setError(errData?.message || errData?.error || 'Failed to set up your account. The invitation may have expired.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-sidebar items-center justify-center p-12 relative overflow-hidden">
        <GradientDots duration={20} colorCycleDuration={8} backgroundColor="#0C1430" />
        <div className="relative z-10">
          <img src="/images/paver-logo.png" alt="Paver" className="mx-auto h-32" />
        </div>
      </div>

      {/* Right panel - registration form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md border-0 shadow-none sm:border sm:shadow-sm">
          <CardHeader className="space-y-1 text-center">
            <img src="/images/paver-logo.png" alt="Paver" className="mx-auto mb-4 h-12 lg:hidden" />
            <CardTitle className="text-2xl font-bold">Set Up Your Account</CardTitle>
            <CardDescription>Create a password to get started</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={!credential}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={!credential}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading || !credential}>
                {loading ? 'Setting up...' : 'Create Account'}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <a href="/login" className="text-primary hover:underline">Sign in</a>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
